import Delete from "@mui/icons-material/Delete";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios, { type AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectOrganizationIdentityProvider, selectUser } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../../SettingsDataGridSelectionRow";
import { getGroupSSOColumns, groupIdpGroupColumns, type GroupIdpGroupRow } from "./columns/GroupSSOColumns";

/** Passed from `GroupWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface GroupNavState {
	name: string;
}

interface GroupSSOProps {
	state?: GroupNavState | null;
}

interface ExternalGroup {
	externalGroupId: string;
	externalGroupName: string;
}

interface IdpGroup {
	externalGroupId: string;
	name: string;
}

export default function GroupSSO({ state: stateFromBoundary }: GroupSSOProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("groupIdpGroups");
	const currentUser = useSelector(selectUser);
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);
	const navigate = useNavigate();
	const { groupId } = useParams<{ groupId: string }>();
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: GroupNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the group. */
	const state = (stateFromBoundary ?? stateFromLocation) as GroupNavState;
	const [addIdPGroupsIsToggled, setAddIdPGroupsIsToggled] = useState<boolean>(false);
	const [addIdpGroupsSearchInput, setAddIdpGroupsSearchInput] = useState<string>("");
	const [idpGroups, setIdpGroups] = useState<ExternalGroup[]>([]);
	const [idpGroupsToAdd, setIdpGroupsToAdd] = useState<IdpGroup[]>([]);
	const [availableIdpGroups, setAvailableIdpGroups] = useState<IdpGroup[]>([]);
	const [groupIdpGroups, setGroupIdpGroups] = useState<GroupIdpGroupRow[]>([]);
	const [selectedIdpGroups, setSelectedIdpGroups] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [editGroupIsToggled, setEditGroupIsToggled] = useState<boolean>(false);
	const [deleteGroupIsToggled, setDeleteGroupIsToggled] = useState<boolean>(false);
	const [removeIdpGroupsIsToggled, setRemoveIdpGroupsIsToggled] = useState<boolean>(false);
	const [groupNameInput, setGroupNameInput] = useState<string>("");
	const [groupNameError, setGroupNameError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(true);
	const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
	
	const removeIdPGroupFromGroup = useCallback((idpGroupId: string | number) => {
		if (!groupId) return;
		
		axios.delete(apiRoutes.removeIdpGroupFromGroup(groupId, idpGroupId), { headers: authHeader })
			.then(() => {
				init();
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.IDP_GROUP_REMOVAL_ERROR_MSG(identityProviderName || "IdP"));
			});
	}, [authHeader, groupId, handleToastAlert, identityProviderName]);

	const columns = useMemo(() => (
		getGroupSSOColumns(
			identityProviderName,
			removeIdPGroupFromGroup
		)
	), [identityProviderName]);

	const addIdPGroupsToGroup = () => {
		if (!groupId) return;
		
		axios.put(apiRoutes.updateGroupIdpGroups(groupId), { idpgroups: idpGroupsToAdd }, { headers: authHeader })
			.then(() => {
				init();
				toggleAddNewDialog();
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.IDP_GROUPS_ADDITION_ERROR_MSG(identityProviderName || "IdP"));
			});
	};

	const addIdPGroupsToSelectedIdpGroups = (value: string[]) => {
		//value is an array of idpGroup names, we need to convert them to idpGroup objects to display the group name properly
		const mappedValue = value.map((idpGroup) => {
			const tempArr = [...groupIdpGroups, ...availableIdpGroups];
			const foundIdpGroup = tempArr.find((row) => row.name === idpGroup);
			return foundIdpGroup;
		}).filter((group): group is IdpGroup => group !== undefined);
		setIdpGroupsToAdd(mappedValue);
	};

	const init = () => {
		if (!groupId) return;
		
		axios.get(apiRoutes.getGroupIdpGroups(groupId), { headers: authHeader })
			.then((response) => {
				setGroupIdpGroups(response.data);
				setLoading(false);
			})
			.catch((error: AxiosError) => {
				console.error(error);
			});
	};

	const initAvailableIdpGroups = (retrievedIdpGroups: ExternalGroup[]) => {
		setOptionsLoading(true);
		const available: IdpGroup[] = [];
		if (retrievedIdpGroups && retrievedIdpGroups.length > 0) {
			retrievedIdpGroups.forEach((idpGroup) => {
				if (!groupIdpGroups.some((groupIdpGroup) => groupIdpGroup.name === idpGroup.externalGroupName)) {
					available.push({ externalGroupId: idpGroup.externalGroupId, name: idpGroup.externalGroupName });
				}
			});
		}
		setAvailableIdpGroups(available);
		setOptionsLoading(false);
	};

	const initIdpGroups = () => {
		if (!currentUser.organizationId) return;
		
		axios.get(apiRoutes.getExternalGroups(currentUser.organizationId), { headers: authHeader })
			.then((response) => {
				setIdpGroups(response.data);
				initAvailableIdpGroups(response.data);
			})
			.catch((error: AxiosError) => {
				console.error(error);
			});
	};

	const bulkRemoveIdpGroupsFromGroup = () => {
		if (!groupId) return;
		
		const idpgroupIds = gridRowSelectionIdsArray(selectedIdpGroups, groupIdpGroups);
		const deleteData = { idpgroupIds };
		const plural = idpgroupIds.length > 1;
		axios.delete(apiRoutes.bulkRemoveIdpGroupsFromGroup(groupId), { headers: authHeader, data: deleteData })
			.then(() => {
				init();
				setRemoveIdpGroupsIsToggled(false);
				setSelectedIdpGroups(emptyGridRowSelectionModel());
			})
			.catch((err: AxiosError) => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.IDP_GROUP_REMOVAL_ERROR_MSG(identityProviderName || "IdP"));
			});
	};

	const toggleAddNewDialog = () => {
		setAddIdPGroupsIsToggled(!addIdPGroupsIsToggled);

		if (!addIdPGroupsIsToggled) {
			setAddIdPGroupsIsToggled(true);
			initIdpGroups();
		} else {
			setAddIdPGroupsIsToggled(false);
			setAvailableIdpGroups([]);
			setIdpGroupsToAdd([]);
			setOptionsLoading(true);
		}
	};

	const handleToggleGroupEditDialog = () => {
		setEditGroupIsToggled(!editGroupIsToggled);
		setGroupNameError("");
		setGroupNameInput("");
	};

	const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setGroupNameInput(e.target.value);
		setGroupNameError("");
	};

	const handleGroupNameChangeSubmit = () => {
		if (!groupNameInput || !groupId) return;

		if (groupNameInput === state.name) {
			handleToggleGroupEditDialog();
			return;
		}

		axios.put(apiRoutes.updateGroup(groupId), { name: groupNameInput }, { headers: authHeader })
			.then(() => {
				setEditGroupIsToggled(false);
				updateCoAppNavigation(false, navigationItemReferences.groupNavItemIndex, groupNameInput, pathname);
			})
			.catch((e: AxiosError) => {
				console.error(e);
				if (e.response?.data && typeof e.response.data === "object" && "message" in e.response.data && 
					typeof e.response.data.message === "string" && e.response.data.message.includes("already exists")) {
					setGroupNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Group"));
					return;
				}
				handleToggleGroupEditDialog();
				handleToastAlert(messageLevels.ERROR, messages.GROUP_NAME_UPDATE_ERROR_MSG);
			});
	};

	const handleToggleDeleteGroupDialog = () => {
		setDeleteGroupIsToggled(!deleteGroupIsToggled);
	};

	const confirmGroupDeletion = () => {
		if (!groupId) return;
		
		axios.delete(apiRoutes.deleteGroup + "/" + groupId, {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.groupNavItemIndex);
			navigate(pages.groupManagement, { replace: true });
			handleToggleDeleteGroupDialog();
		}).catch((err: AxiosError) => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
			handleToggleDeleteGroupDialog();
		});
	};

	useEffect(() => {
		init();
		handleInitializeSortModel();
	}, [state.name]);

	const idpGroupNoun = identityProviderName ? `${identityProviderName} group` : "IdP group";
	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(groupIdpGroups, loading);
	const selectedIdpGroupCount = resolveGridSelectedRowIds(selectedIdpGroups, groupIdpGroups).length;

	return (
		<>
			<CoAppDatagridHeader
				title={`${state.name} ${identityProviderName ? identityProviderName + " " : "IdP "}Group Management`}
				subResourceType={identityProviderName ? `${identityProviderName} Group` : "IdP Group"}
				resourceType="Group"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				addOnClickHandler={toggleAddNewDialog}
				addIsPresent={true}
				editIsPresent={true}
				editOnClickHandler={handleToggleGroupEditDialog}
				deleteIsPresent={true}
				deleteOnClickHandler={handleToggleDeleteGroupDialog}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={groupIdpGroups}
				allowSelection={true}
				pinnedColumns={["actions"]}
				targetResource={identityProviderName ? `${identityProviderName} groups` : "IdP groups"}
				parentResource="group"
				parentResourceName={state.name}
				selectionModel={selectedIdpGroups}
				setSelectionModel={setSelectedIdpGroups}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun={idpGroupNoun}
						visibleCount={visibleRowCount}
						selectedCount={selectedIdpGroupCount}
						loading={loading}
						onClearSelection={() => setSelectedIdpGroups(emptyGridRowSelectionModel())}
						actions={[{
							label: "Remove",
							icon: <Delete sx={destructiveIconSx} />,
							onClick: () => setRemoveIdpGroupsIsToggled(true),
							variant: "destructive",
						}]}
					/>
				}
			/>
			<CoAppAddItemsToListModal
				addButtonTitle="Save"
				inputLabel={identityProviderName ? `${identityProviderName} Group(s)` : "IdP Group(s)"}
				addItemsIsToggled={addIdPGroupsIsToggled}
				addItemsToCollection={addIdPGroupsToGroup}
				addItemsToNewArray={addIdPGroupsToSelectedIdpGroups}
				dialogSearchPlaceholder={`Search ${identityProviderName ? identityProviderName + " " : ""}Groups`}
				dialogTitle={`Add ${identityProviderName ? identityProviderName + " " : ""}Group(s) to ${state.name} group`}
				items={availableIdpGroups}
				itemsToAdd={idpGroupsToAdd}
				searchInput={addIdpGroupsSearchInput}
				setSearchInput={setAddIdpGroupsSearchInput}
				toggleDialog={toggleAddNewDialog}
				loading={optionsLoading}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteGroupIsToggled}
				dialogTitle={`Delete ${state.name} group?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "group")}
				confirmClickHandler={confirmGroupDeletion}
				cancelClickHandler={handleToggleDeleteGroupDialog}
				actionText="Delete"
			/>

			<CoAppDestructiveConfirmationModal
				dialogOpen={removeIdpGroupsIsToggled}
				dialogTitle={`Remove selected (${gridRowSelectionSize(selectedIdpGroups, groupIdpGroups)}) ${identityProviderName ? `${identityProviderName} ` : "IdP "}${gridRowSelectionSize(selectedIdpGroups, groupIdpGroups) > 1 ? "groups" : "group"} from ${state.name}?`}
				dialogMessage={messages.IDP_GROUP_REMOVAL_MODAL_MSG(identityProviderName || "IdP", state.name, "group")}
				confirmClickHandler={bulkRemoveIdpGroupsFromGroup}
				cancelClickHandler={() => setRemoveIdpGroupsIsToggled(false)}
				actionText="Remove"
			/>
			<CoAppEditNameModal
				dialogOpen={editGroupIsToggled}
				dialogTitle="Edit group name"
				changeHandler={handleGroupNameChange}
				placeholderText={state.name}
				confirmClickHandler={handleGroupNameChangeSubmit}
				cancelClickHandler={handleToggleGroupEditDialog}
				actionText="Save"
				editNameError={groupNameError}
				setEditNameError={setGroupNameError}
			/>
		</>
	);
}